<template>
  <div class="view-only-map">
    <div class="map-container">
      <GoogleMap 
        ref="mapRef" 
        :center="center" 
        :zoom="7" 
        style="width: 100%; height: 600px;"
        :options="{ clickableIcons: false, gestureHandling: 'greedy' }"
      >
        <!-- Display the hunt boundary polygon if available -->
        <Polygon 
          v-if="huntBoundaryPaths.length > 0 && huntBoundaryPaths[0].length >= 3"
          :paths="huntBoundaryPaths"
          :options="{
            fillColor: '#FF5722',
            fillOpacity: 0.35,
            strokeColor: '#FF5722',
            strokeWeight: 2
          }" 
        />
      </GoogleMap>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Map as GoogleMap, Polygon } from '@fawmi/vue-google-maps'

export default defineComponent({
  name: 'ViewOnlyMap',
  components: {
    GoogleMap,
    Polygon
  },
  data() {
    return {
      // Center coordinates for Utah (approximately Salt Lake City)
      center: { lat: 39.3199, lng: -111.0937 },
      huntBoundaryPaths: [] // Array of arrays of lat/lng points for hunt boundary (outer ring + holes)
    }
  },
  methods: {
    /**
     * Displays a polygon on the map using the provided paths
     * @param {Array} paths - Array of arrays of {lat, lng} objects. First array is outer boundary, rest are holes.
     */
    displayPolygon(paths) {
      if (!paths || !Array.isArray(paths) || paths.length === 0 || !paths[0] || paths[0].length < 3) {
        this.huntBoundaryPaths = [];
        return;
      }

      this.huntBoundaryPaths = paths;

      // If the map is already loaded, fit bounds to show the polygon
      this.$nextTick(() => {
        const mapInstance = this.$refs.mapRef?.$mapObject;
        if (mapInstance && this.huntBoundaryPaths.length > 0 && this.huntBoundaryPaths[0].length >= 3) {
          // Calculate the center of the polygon using the outer boundary (first path)
          const bounds = new google.maps.LatLngBounds();
          this.huntBoundaryPaths[0].forEach(point => {
            bounds.extend(new google.maps.LatLng(point.lat, point.lng));
          });

          // Get the center of the hunt boundary
          const huntCenter = bounds.getCenter();

          // Optionally set a specific zoom first
          mapInstance.setZoom(6.5);

          // Wait for the map to be idle after zoom change
          google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
            // Get the map's height in pixels
            const mapHeight = mapInstance.getDiv().offsetHeight;

            // We want the center to appear 30% from the top
            const desiredPositionFromTop = mapHeight * 0.3;

            // Offset needed from the true center (50% from top) to 30% from top
            const offset = (mapHeight / 2) - desiredPositionFromTop;

            // Convert that offset in pixels to an offset in latitude
            const projection = mapInstance.getProjection();
            if (projection) {
              const scale = Math.pow(2, mapInstance.getZoom());

              // Convert the hunt boundary center to "world" coords
              const worldCenter = projection.fromLatLngToPoint(huntCenter);

              // Convert our pixel offset to the same scale as "world" coordinates
              const pixelOffsetInWorldCoords = offset / scale;

              // Create a new point with the adjusted y value
              const adjustedWorldPoint = new google.maps.Point(
                worldCenter.x,
                worldCenter.y + pixelOffsetInWorldCoords
              );

              // Convert back to a LatLng and set the map center
              const adjustedLatLng = projection.fromPointToLatLng(adjustedWorldPoint);
              mapInstance.setCenter(adjustedLatLng);
            } else {
              // Fallback if no projection is available
              mapInstance.setCenter(huntCenter);
            }
          });

          // Initial center (will be adjusted after 'idle')
          mapInstance.setCenter(huntCenter);
        }
      });
    }
  }
})
</script>

<style scoped>
.view-only-map {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
}

.map-container {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}
</style>
